import { useCallback } from "react";
import { useNavigate, useMatch } from "react-router-dom";

interface UseModalRouteOptions {
  /**
   * The route pattern to match (e.g., "/classes/new" or "/classes/:classId/edit").
   * Supports React Router path parameters.
   */
  pattern: string;
  /**
   * The parent route to navigate to when closing.
   * Can include path parameters that will be filled from the current match.
   * Example: "/classes/:classId" when pattern is "/classes/:classId/edit"
   */
  parentPath: string;
}

interface UseModalRouteResult {
  /** Whether the modal should be open based on current route */
  isOpen: boolean;
  /** Function to open the modal by navigating to its route */
  open: (params?: Record<string, string>) => void;
  /** Function to close the modal by navigating to parent route */
  close: () => void;
  /** The matched route parameters (if any) */
  params: Record<string, string | undefined>;
}

/**
 * Hook for managing modal state via URL routes.
 *
 * Instead of using useState to control modal visibility, this hook
 * derives modal state from the current URL and provides navigation
 * functions for opening/closing.
 *
 * Benefits:
 * - Deep-linkable: Users can copy/paste URLs to open modals
 * - Browser history: Back button closes modals naturally
 * - Shareable: Links work across tabs/sessions
 *
 * @example
 * // Simple modal (no ID)
 * const { isOpen, open, close } = useModalRoute({
 *   pattern: "/classes/new",
 *   parentPath: "/classes"
 * });
 *
 * // In JSX:
 * <button onClick={() => open()}>Create Class</button>
 * <Modal isOpen={isOpen} onClose={close}>...</Modal>
 *
 * @example
 * // Modal with ID parameter
 * const { isOpen, open, close, params } = useModalRoute({
 *   pattern: "/classes/:classId/edit",
 *   parentPath: "/classes/:classId"
 * });
 *
 * // Open with specific ID:
 * open({ classId: "abc123" });
 *
 * // Access the ID in the modal:
 * const classId = params.classId;
 *
 * @example
 * // Modal with multiple parameters
 * const { isOpen, open, close, params } = useModalRoute({
 *   pattern: "/classes/:classId/students/:studentId",
 *   parentPath: "/classes/:classId"
 * });
 *
 * open({ classId: "abc", studentId: "xyz" });
 */
export function useModalRoute({
  pattern,
  parentPath,
}: UseModalRouteOptions): UseModalRouteResult {
  const navigate = useNavigate();
  const match = useMatch(pattern);

  const isOpen = match !== null;
  const params = (match?.params as Record<string, string | undefined>) ?? {};

  const open = useCallback(
    (openParams?: Record<string, string>) => {
      let path = pattern;
      if (openParams) {
        Object.entries(openParams).forEach(([key, value]) => {
          path = path.replace(`:${key}`, value);
        });
      }
      void navigate(path);
    },
    [navigate, pattern]
  );

  const close = useCallback(() => {
    // Navigate to parent path, replacing dynamic segments with matched values
    let path = parentPath;
    if (match?.params) {
      Object.entries(match.params).forEach(([key, value]) => {
        if (value) path = path.replace(`:${key}`, value);
      });
    }
    void navigate(path);
  }, [navigate, parentPath, match?.params]);

  return { isOpen, open, close, params };
}
